/**
 * Water-source normalization + semantics — the SINGLE source of truth for
 * reading `ProjectIntent.waterSource`.
 *
 * The intent editor lets an operator declare MORE than one water supply, so the
 * canonical field is the `sources` array. This module resolves that array while
 * staying back-compatible with:
 *   - persisted sites written before the multi-select migration (legacy single
 *     `source` scalar), and
 *   - single-value context ingestion, which still fills the legacy `source`.
 *
 * Every consumer (risk seeds, report verdict, path-to-GO blockers, fidelity
 * scoring, report prompts, coverage) MUST read through these helpers rather than
 * touching `.source` / `.sources` directly, so the drought-exposure rule lives
 * in exactly one place.
 */
import type { ProjectIntent, WaterSourceKind } from '../../types/siteRag';

type WaterSourceField = ProjectIntent['waterSource'];

/** Canonical ordering for stable, deterministic display + ids. */
const WATER_SOURCE_ORDER: readonly WaterSourceKind[] = [
  'municipal',
  'groundwater',
  'surface',
  'reuse',
];

/** Sources that draw a raw/natural body and are therefore drought-exposed. */
const DROUGHT_EXPOSED_KINDS: ReadonlySet<WaterSourceKind> = new Set<WaterSourceKind>([
  'surface',
  'groundwater',
]);

/**
 * The effective, de-duplicated water sources for an intent, in canonical order.
 *
 * Resolves the canonical `sources` array first; when it is absent (legacy /
 * ingestion-filled data) it falls back to the single `source` scalar. `'none'`
 * and unknown values are dropped, so an empty array is the honest "undecided"
 * signal. For a single-source intent this returns exactly `[source]`, keeping
 * every downstream predicate byte-identical to the pre-migration behaviour.
 */
export function waterSourceKinds(ws: WaterSourceField): WaterSourceKind[] {
  if (!ws) return [];
  const raw = ws.sources && ws.sources.length > 0
    ? ws.sources
    : (ws.source && ws.source !== 'none' ? [ws.source] : []);
  const seen = new Set<WaterSourceKind>();
  for (const kind of raw) {
    if (WATER_SOURCE_ORDER.includes(kind as WaterSourceKind)) {
      seen.add(kind as WaterSourceKind);
    }
  }
  return WATER_SOURCE_ORDER.filter((k) => seen.has(k));
}

/** True when the operator has declared at least one water source. */
export function hasDeclaredWaterSource(ws: WaterSourceField): boolean {
  return waterSourceKinds(ws).length > 0;
}

/**
 * Drought-curtailment exposure predicate — the domain rule for the multi-select.
 *
 * Semantics: exposure exists when ANY declared source draws a raw body
 * (surface / groundwater) and the water right is not permitted. A single
 * unpermitted surface intake is a real curtailment path even alongside a firmer
 * supply, so the presence of one exposed source is enough. Changing this rule
 * (e.g. to require EVERY source be exposed) is a one-line edit here — that is
 * the point of centralizing it.
 */
export function hasDroughtExposedSource(ws: WaterSourceField): boolean {
  return waterSourceKinds(ws).some((k) => DROUGHT_EXPOSED_KINDS.has(k));
}

/**
 * ONLY the declared sources that actually draw a raw body (2026-08-14).
 *
 * Prose about drought exposure must name THESE, not every declared source.
 * With `['municipal','surface','reuse']` the blocker read "An unpermitted
 * municipal, surface water, reuse water source is exposed to drought
 * curtailment" — asserting curtailment risk for a municipal tie-in and a
 * reclaimed supply, which is factually wrong and reads as sloppy to a
 * capital committee. The exposure PREDICATE is unchanged (one exposed
 * source is enough); only the naming narrows.
 */
export function droughtExposedKinds(ws: WaterSourceField): WaterSourceKind[] {
  return waterSourceKinds(ws).filter((k) => DROUGHT_EXPOSED_KINDS.has(k));
}

/** Human-readable label of just the drought-exposed sources ('' when none). */
export function formatDroughtExposedSources(
  ws: WaterSourceField,
  labels?: Partial<Record<WaterSourceKind, string>>,
): string {
  return droughtExposedKinds(ws).map((k) => labels?.[k] ?? k).join(', ');
}

/**
 * De-risked-supply predicate for the cooling fidelity credit: a reclaimed/reuse
 * source, or a permitted municipal supply, is a firmer water basis. Mirrors the
 * pre-migration `reuse || (permitted && municipal)` credit.
 */
export function hasDeRiskedSource(ws: WaterSourceField): boolean {
  const kinds = waterSourceKinds(ws);
  if (kinds.includes('reuse') && ws?.rightsStatus !== 'none' && ws?.rightsStatus !== 'applied') return true;
  return ws?.rightsStatus === 'permitted' && kinds.includes('municipal');
}

/**
 * Human-readable joined label of the declared sources (e.g. "surface, municipal"),
 * or `''` when undecided. `labels` optionally maps each kind to a display string.
 */
export function formatWaterSources(
  ws: WaterSourceField,
  labels?: Partial<Record<WaterSourceKind, string>>,
): string {
  return waterSourceKinds(ws)
    .map((k) => labels?.[k] ?? k)
    .join(', ');
}
